<template>
    <div class="mt-5 mb-10">
        <h1 class="page-title">{{ $t("catalogue.registrationNumber.title") }}</h1>
    </div>

    <div v-if="isLoading" class="flex justify-content-center">
        <ProgressSpinner aria-label="Chargement" />
    </div>
    <div v-else class="form-container">
        <div class="mx-auto shadow-md p-6 rounded-lg w-[90%] md:w-[80%] lg:w-[50%]">
            <div class="justify-center">
                <FormKit type="form"
                     id="company-matricule-import-form"
                     :actions="false"
                     :model="form"
                     @submit="onSubmit"
                     outer-class="w-full"
                     incomplete-message="Veuillez remplir tous les champs correctement"
                     #default="{ state: { valid } }"
                >
                    <FormKit type="select"
                        name="importType"
                        v-model="form.importType"
                        label="Action*"
                        validation="required"
                        :validation-messages="{required: 'Veuillez selectionner Activer ou Désactiver.'}"
                        :options="[
                            { label: 'Activer', value: 'enable' },
                            { label: 'Désactiver', value: 'disable' }
                        ]"
                    />
                    <FormKit type="autocomplete"
                        name="companyUuid"
                        v-model="form.companyUuid"
                        label="Entreprise*"
                        placeholder="Sélectionner une société"
                        validation="required"
                        empty-message="Aucune propriété trouvée"
                        :options="searchCompanyOptions"
                        :validation-messages="{required: 'Veuillez sélectionner une société.'}"
                    />
                    <FormKit type="file"
                        name="file"
                        label="Fichier CSV*"
                        help="Sélectionnez le fichier à uploader."
                        accept=".csv"
                        placeholder="Sélectionnez un fichier."
                        validation="required|validateFileExtension:csv"
                        :validation-messages="{
                            required: 'Aucun fichier sélectionné.',
                            validateFileExtension: 'Le fichier doit être au format CSV.'
                        }"
                    />
                    <div class="flex mt-[1.5rem]">
                        <FormKit type="submit" label="Importer" :disabled="!valid" class="btn" />
                    </div>
                </FormKit>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import { ICompanyMatriculeImportForm } from "@/models/companyMatricule/ICompanyMatriculeImportForm";
import { getPaginatedCompanyForDropDown } from '@/_services/company/company';
import ProgressSpinner from "primevue/progressspinner";
import router from "@/router";

const form = ref<ICompanyMatriculeImportForm>({
    file: null,
    companyUuid: '',
    importType: 'enable'
});
const isLoading = ref<boolean>(false);

async function searchCompanyOptions({ search, page, hasNextPage }) {
    const [totalCompaniesRecordsForDropDown, companiesForDropDown] = await getPaginatedCompanyForDropDown(5, page, search, true);

    const hasItems = companiesForDropDown.length > 0;
    if (hasItems && page !== totalCompaniesRecordsForDropDown) {
        hasNextPage();
    }

    return companiesForDropDown;
}

const showSpinner = () => {
    isLoading.value = true;
}

const hideSpinner = () => {
    isLoading.value = false;
}

const onSubmit = async (formValue, formNode) => {
    try {
        const importType = formValue.importType;
        const action = importType === 'enable' ? 'activer' : 'désactiver';
        const file = formValue.file?.[0]?.file;

        if (!file) {
            await swal.error('Aucun fichier sélectionné.');
            return;
        }

        const filename = file.name;
        if (!filename.endsWith(".csv")) {
            await swal.error('Le fichier doit être au format CSV.');
            return;
        }
        
        const responseLevel1 = await swal.confirm(
            `Êtes-vous sûr de vouloir ${action} ces matricules ?`,
            "Annuler",
            "Importer"
        );

        if (!responseLevel1.isConfirmed) {
            return;
        }

        const payload = new FormData();
        payload.append('file', file, filename);
        payload.append('companyUuid', formValue.companyUuid);
        payload.append('importType', importType);

        showSpinner();
        try {
            const { data } = await ApiService.post("admin/company-matricule/import-csv", payload);

            await swal.success(
                'Succès',
                "Les matricules sont en attente d'import, vous serez notifié par mail de leur traitement."
            );

            window.location.reload();
        } catch (error: any) {
            let errorMsg = "Une erreur est survenue";
            if (error.response?.data?.errors) {
                error.response.data.errors.forEach((err) => {
                    console.error('Error:', err.message);
                });

                if (error.response.data.errors[0]?.message) {
                    errorMsg = error.response.data.errors[0].message;
                }
            } else if (!error.response) {
                errorMsg = "Erreur de connexion au serveur";
            }

            await swal.error(errorMsg);
            window.location.reload();
        } finally {
            hideSpinner();
        }

    } catch (error) {
        console.error('Unexpected error in onSubmit:', error);
        hideSpinner();
        await swal.error('Une erreur inattendue est survenue.');
    }
};
</script>

<style scoped lang="scss">
    form {
        h2 {
            color: #000;
        }
        ::v-deep(.formkit-select-icon) {
            height: 50px;
            margin: 0;
            padding: 0;
        }
    }
    main .container .card {
        background-color: rgba(91, 91, 98, 0.08);
    }

    .formkit-option {
        background-color: red !important;
    }

    select {
        background-color: pink;
    }
</style>
